import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { toWireIds } from '../shared/wire-ids';
import { requireWorkspaceId } from '../shared/workspace';
import {
  newsletterCatalogKey,
  newsletterKeys,
  parseNewsletter,
  type Newsletter,
  type NewsletterCatalogScope,
  type NewsletterListParams,
} from './entity';

const STALE_TIME = 60_000;

/** The newsletters list plus the workspace total (pagination meta or page length). */
export interface NewslettersList {
  newsletters: Newsletter[];
  total: number;
}

/** Parses a list response's rows and sideloads each into its detail slot. */
function sideloadNewsletters(workspaceId: number, rows: Newsletter[] | undefined, qc: QueryClient): Newsletter[] {
  const newsletters = (rows ?? []).map(parseNewsletter);
  for (const newsletterRow of newsletters) {
    qc.setQueryData(newsletterKeys.detail(workspaceId, newsletterRow.id), newsletterRow);
  }
  return newsletters;
}

/**
 * Workspace newsletters (broadcasts) list. Each row is sideloaded into its
 * detail slot so a follow-up `useNewsletterOptional` resolves from cache.
 * `total` prefers the pagination meta and falls back to the page length.
 *
 * @public
 */
export async function getNewsletters(workspaceId: number, qc: QueryClient): Promise<NewslettersList> {
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return { newsletters, total: raw.meta?.pagination?.total ?? newsletters.length };
}

/** @public */
export const getNewslettersQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: newsletterKeys.list(workspaceId, undefined),
    queryFn: () => getNewsletters(workspaceId, qc),
    staleTime: STALE_TIME,
  });

type NewslettersListQuery = NonNullable<
  NonNullable<NonNullable<Parameters<typeof hydraUiApi.newsletters.list>[0]>['params']>['query']
>;

/* ------------------------------------------------------------------------- */
/* Broadcasts newsletters list — filtered, non-cached                         */
/* ------------------------------------------------------------------------- */

/**
 * The per-status counts the list response carries alongside the page.
 *
 * These count the *current* filter set, not the workspace: the handler
 * recomputes them after applying the date filter, so they move with the
 * selected range. `all_time` is the exception, and is what the welcome empty
 * state keys off.
 */
export interface NewsletterTotals {
  sent: number;
  drafted: number;
  scheduled: number;
  all_time: number;
}

/** One page of the filtered newsletters list, with the server's meta. */
export interface NewslettersPage {
  newsletters: Newsletter[];
  pagination: { page: number; size: number; total: number };
  totals: NewsletterTotals;
}

function toNewslettersListQuery(params: NewsletterListParams): NewslettersListQuery {
  return {
    page: params.page - 1, // 1-based at the hook boundary, 0-based on the wire
    channel: params.channel,
    type: params.type,
    search: params.search,
    sort: params.sort,
    asc: params.asc,
    start_time: params.startTime,
    end_time: params.endTime,
    sendToUnsubscribed: params.sendToUnsubscribed,
    subscriptionTopicId: params.subscriptionTopicId,
    'tagIds[]': toWireIds(params.tagIds),
    'objectTypeIds[]': toWireIds(params.objectTypeIds),
    'goalIds[]': toWireIds(params.goalIds),
  };
}

/**
 * One page of the Broadcasts newsletters list.
 *
 * Every row is sideloaded into its detail slot, so a follow-up
 * `useNewsletterOptional` resolves from cache without a second request.
 *
 * @public
 */
export async function getFilteredNewsletters(
  workspaceId: number,
  params: NewsletterListParams,
  qc: QueryClient,
): Promise<NewslettersPage> {
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) }, query: toNewslettersListQuery(params) },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return {
    newsletters,
    // Read as-is: the handler always writes both, and the spec marks them
    // required. A fallback could only invent numbers, and a zeroed `all_time`
    // is exactly what reads as "no newsletters yet" to the welcome state.
    // "Unknown" is the page's call, from a pending or failed query.
    pagination: raw.meta.pagination,
    totals: raw.meta.totals,
  };
}

/**
 * Never cached. Ember reaches this data through `store.query`, which always
 * hits the server — right for a searched, filtered, sorted, paged list where
 * a cached page would show a stale row set, and where mutations made
 * elsewhere in the app land without this list observing them.
 *
 * @public
 */
export const getFilteredNewslettersQueryOptions = (
  workspaceId: number,
  params: NewsletterListParams,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: newsletterKeys.list(workspaceId, params),
    queryFn: () => getFilteredNewsletters(workspaceId, params, qc),
    staleTime: 0,
  });

interface UseFilteredNewslettersOptions {
  workspaceId?: number;
  params: NewsletterListParams;
  queryConfig?: QueryConfig<typeof getFilteredNewslettersQueryOptions>;
}

/**
 * The Broadcasts newsletters tab's list read.
 *
 * Non-suspense on purpose. The Ember page degrades a failed list fetch to an
 * empty tab rather than routing to the app-wide error substate, and it tells
 * "not loaded" apart from "loaded and empty" when deciding to show the
 * welcome state. Both need the error and pending flags in hand, which a
 * suspense read hands to a boundary instead.
 *
 * @public
 */
export const useFilteredNewsletters = ({
  workspaceId: overrideWorkspaceId,
  params,
  queryConfig,
}: UseFilteredNewslettersOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({
    ...getFilteredNewslettersQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

/** One page of the newsletter catalog plus the server's pagination meta. */
export interface NewsletterCatalogPage {
  newsletters: Newsletter[];
  /** Rows matching the scope across all pages, from the pagination meta. */
  total: number;
  /** The server's page size (the endpoint fixes it; the response reports it). */
  size: number;
}

/**
 * One server page of workspace newsletters, optionally name-searched
 * (case-insensitive substring; `page` is 0-based). Each row is sideloaded
 * into its detail slot so a picker's by-id name lookup resolves from cache.
 *
 * @public
 */
export async function getNewslettersPage(
  workspaceId: number,
  { page, search }: NewsletterCatalogScope & { page: number },
  qc: QueryClient,
): Promise<NewsletterCatalogPage> {
  const query: NewslettersListQuery = { page };
  if (search != null) {
    query.search = search;
  }
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) }, query },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return {
    newsletters,
    total: raw.meta?.pagination?.total ?? newsletters.length,
    size: raw.meta?.pagination?.size ?? newsletters.length,
  };
}

/**
 * The server-paged newsletter catalog for pickers. The first page loads on
 * mount; callers pull further pages with `fetchNextPage`. A page shorter than
 * the server's page size (or empty) ends the catalog — the same two stop
 * conditions the legacy editor derives from the pagination meta. A changed
 * search term is a new catalog starting from page 0; the previous term's
 * pages stay visible while the first page loads, so a keystroke doesn't blank
 * the picker.
 *
 * @public
 */
export function useNewsletterCatalog(workspaceId: number, scope: NewsletterCatalogScope = {}) {
  const qc = useQueryClient();
  return useInfiniteQuery({
    queryKey: newsletterCatalogKey(workspaceId, scope),
    queryFn: ({ pageParam }) => getNewslettersPage(workspaceId, { page: pageParam, ...scope }, qc),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.newsletters.length === 0 || lastPage.newsletters.length < lastPage.size ? undefined : lastPageParam + 1,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/** @public */
export async function getNewsletter(workspaceId: number, newsletterId: string, qc: QueryClient): Promise<Newsletter> {
  const raw = await hydraUiApi.newsletters.get({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
  if (raw.newsletter == null) {
    throw new Error(`newsletter ${newsletterId} not found`);
  }
  const parsed = parseNewsletter(raw.newsletter);
  qc.setQueryData(newsletterKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const getNewsletterQueryOptions = (workspaceId: number, newsletterId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: newsletterKeys.detail(workspaceId, newsletterId),
    queryFn: () => getNewsletter(workspaceId, newsletterId, qc),
    staleTime: STALE_TIME,
  });

interface UseNewsletterOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useSnippetOptional`).
 *
 * @public
 */
export const useNewsletterOptional = ({ workspaceId, id, enabled = true }: UseNewsletterOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? newsletterKeys.detail(workspaceId, id) : ['newsletter', 'inactive'],
    queryFn: active ? () => getNewsletter(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};
